/**
 * XMLHttpRequest interceptor for CSV data
 *
 * Monkey-patches XMLHttpRequest to intercept CSV responses.
 * The original XHR behavior is preserved.
 */

import type { InterceptionConfig } from '../types/config';
import type { DataSource } from '../types/api';
import type {
  Interceptor,
  InterceptionHandler,
  InterceptedCSV,
} from './types';
import {
  isCSVMimeType,
  isCSVFilename,
  looksLikeCSV,
  hashCSV,
  extractFilename,
  suggestTableName,
  matchesUrlPattern,
} from './types';
import { v4 as uuidv4 } from 'uuid';

/**
 * XHR interceptor class
 */
export class XHRInterceptor implements Interceptor {
  private OriginalXHR: typeof XMLHttpRequest;
  private active: boolean = false;
  private config: Required<InterceptionConfig>;
  private handler: InterceptionHandler;

  constructor(config: Required<InterceptionConfig>, handler: InterceptionHandler) {
    this.OriginalXHR = window.XMLHttpRequest;
    this.config = config;
    this.handler = handler;
  }

  /**
   * Start intercepting XHR requests
   */
  start(): void {
    if (this.active) {
      console.warn('[XHRInterceptor] Already active');
      return;
    }

    console.log('[XHRInterceptor] Starting interception');

    const self = this;

    // Create a patched XMLHttpRequest class
    class PatchedXHR extends self.OriginalXHR {
      private _url: string = '';
      private _method: string = '';

      constructor() {
        super();
        this._setupInterception();
      }

      open(method: string, url: string | URL, ...args: any[]): void {
        this._method = method;
        this._url = typeof url === 'string' ? url : url.href;

        // Call original open
        return super.open(method, url, ...args as [boolean?, string?, string?]);
      }

      private _setupInterception(): void {
        // Intercept the load event
        this.addEventListener('load', () => {
          this._onLoad();
        });
      }

      private _onLoad(): void {
        // Check if we should intercept this URL
        if (!self.shouldIntercept(this._url)) {
          return;
        }

        // Check if response is CSV
        const contentType = this.getResponseHeader('Content-Type');
        const contentDisposition = this.getResponseHeader('Content-Disposition');

        if (self.isCSVResponse(contentType, this._url, contentDisposition)) {
          // Get response data
          let data: string | null = null;

          if (typeof this.response === 'string') {
            data = this.response;
          } else if (typeof this.responseText === 'string') {
            data = this.responseText;
          } else if (this.response instanceof Blob) {
            // Handle blob response asynchronously
            this.response.text().then(text => {
              self.processResponse(text, this._url, this._method, contentDisposition).catch(
                error => {
                  console.error('[XHRInterceptor] Error processing blob response:', error);
                }
              );
            });
            return;
          }

          if (data) {
            // Process in background
            self.processResponse(data, this._url, this._method, contentDisposition).catch(
              error => {
                console.error('[XHRInterceptor] Error processing response:', error);
              }
            );
          }
        }
      }
    }

    // Replace global XMLHttpRequest
    window.XMLHttpRequest = PatchedXHR as any;

    this.active = true;
  }

  /**
   * Stop intercepting and restore original XHR
   */
  stop(): void {
    if (!this.active) {
      return;
    }

    console.log('[XHRInterceptor] Stopping interception');

    // Restore original XMLHttpRequest
    window.XMLHttpRequest = this.OriginalXHR;
    this.active = false;
  }

  /**
   * Check if currently intercepting
   */
  isActive(): boolean {
    return this.active;
  }

  /**
   * Check if we should intercept this URL
   */
  private shouldIntercept(url: string): boolean {
    // Check ignore patterns first
    if (this.config.ignorePatterns.length > 0) {
      if (matchesUrlPattern(url, this.config.ignorePatterns)) {
        return false;
      }
    }

    // Check URL patterns
    return matchesUrlPattern(url, this.config.urlPatterns);
  }

  /**
   * Check if response looks like CSV
   */
  private isCSVResponse(
    contentType: string | null,
    url: string,
    contentDisposition: string | null
  ): boolean {
    // Check MIME type
    if (isCSVMimeType(contentType)) {
      return true;
    }

    // Check if MIME type is in our configured list
    if (contentType) {
      const normalized = contentType.toLowerCase().split(';')[0].trim();
      if (this.config.mimeTypes.some(type => normalized === type)) {
        return true;
      }
    }

    // Check filename in Content-Disposition
    const filename = extractFilename(url, contentDisposition);
    if (isCSVFilename(filename)) {
      return true;
    }

    // Check URL
    if (isCSVFilename(url)) {
      return true;
    }

    return false;
  }

  /**
   * Process a CSV response
   */
  private async processResponse(
    data: string,
    url: string,
    method: string,
    contentDisposition: string | null
  ): Promise<void> {
    try {
      // Skip empty responses
      if (!data || data.trim().length === 0) {
        return;
      }

      // Final heuristic check - does it look like CSV?
      if (!looksLikeCSV(data)) {
        console.log('[XHRInterceptor] Data does not look like CSV, skipping');
        return;
      }

      // Extract metadata
      const filename = extractFilename(url, contentDisposition);
      const suggestedName = suggestTableName(filename);

      // Create intercepted CSV object
      const intercepted: InterceptedCSV = {
        id: uuidv4(),
        data,
        size: data.length,
        source: {
          type: 'network',
          url,
          filename,
          method,
          contentType: 'text/csv', // We know it's CSV by this point
        },
        interceptedVia: 'xhr',
        timestamp: Date.now(),
        hash: hashCSV(data),
        suggestedName,
      };

      console.log('[XHRInterceptor] CSV intercepted:', {
        url,
        size: data.length,
        filename,
        suggestedName,
      });

      // Call handler
      await this.handler(intercepted);
    } catch (error) {
      console.error('[XHRInterceptor] Error processing CSV:', error);
    }
  }
}

/**
 * Create an XHR interceptor
 */
export function createXHRInterceptor(
  config: Required<InterceptionConfig>,
  handler: InterceptionHandler
): Interceptor {
  return new XHRInterceptor(config, handler);
}
